import Icon from '../Icon.jsx';
import { ArrowLink, ButtonLink, ClosingBand } from '../parts.jsx';
import { appLinks } from '../navigation.js';

const journeys = [
  {
    id: 'book-a-repair', icon: 'wrench', title: 'Book a Repair', line: 'Show us the problem. Choose who fixes it.', link: { label: 'Book a repair', to: '/home-services' }, band: 'white', cards: 'linen',
    steps: [
      ['Take a photo', 'Show us what is wrong.'],
      ['Answer up to three questions', 'Tap your answers. No forms to fill in.'],
      ['Compare quotes', 'Checked local tradespeople send a price and a time.'],
      ['Accept a quote', 'Nothing is booked until you choose.'],
      ['Track the repair', 'Message the trade. Check the status until it is fixed.'],
    ],
  },
  {
    id: 'for-trades-and-services', icon: 'hammer', title: 'For Trades & Services', line: 'Find repair jobs near you. No commission on jobs.', link: { label: 'Find jobs', to: appLinks.findJobs }, band: 'linen', cards: 'white',
    steps: [
      ['Receive requests', 'Get repair jobs near you, with photos and answers.'],
      ['Send quotes', 'Send a price and a time.'],
      ['Win the work', 'The resident chooses you. Message them and book the visit.'],
    ],
  },
  {
    id: 'for-housing-providers', icon: 'building-2', title: 'For Housing Providers', line: 'Get repair requests from residents.', link: { label: 'See housing provider tools', to: '/for-housing-providers' }, band: 'white', cards: 'linen',
    steps: [
      ['Residents report a repair', 'They add a photo and answer up to three questions.'],
      ['We send it to you', 'It goes to your repairs team. BedMaps providers get it in BedMaps.'],
      ['Get quotes', 'Post a job and get quotes from checked local trades.'],
    ],
  },
];

const jumpLinks = [
  ['wrench', 'Book a Repair', 'book-a-repair'],
  ['hammer', 'For Trades & Services', 'for-trades-and-services'],
  ['building-2', 'For Housing Providers', 'for-housing-providers'],
  ['house', 'Self refer', 'self-refer'],
];

const questions = [
  ['Do I have to accept a quote?', 'No. Nothing is booked until you choose.'],
  ['Can you stop contacting my landlord?', 'Yes. Choose “Never contact my landlord” when you report a repair.'],
  ['Who sees my address?', 'Only the tradesperson you choose. Others only see your general area.'],
  ['Do trades pay commission?', 'No. There is no commission on jobs.'],
];

function Journey({ journey }) {
  return <section className={`mk-band is-${journey.band}`} id={journey.id} aria-labelledby={`${journey.id}-title`}>
    <div className="mk-container mk-stack">
      <div className="mk-journey-head">
        <span className="mk-icon-tile is-56"><Icon name={journey.icon} size={28}/></span>
        <div className="mk-journey-text">
          <h2 className="mk-h3 is-32" id={`${journey.id}-title`}>{journey.title}</h2>
          <p className="mk-journey-line">{journey.line}</p>
        </div>
        <ArrowLink to={journey.link.to} className="mk-journey-link">{journey.link.label}</ArrowLink>
      </div>
      <ol className={`mk-journey-steps is-${journey.steps.length} is-${journey.cards}`}>
        {journey.steps.map(([title, line], index) => <li key={title} className="mk-journey-step">
          <span className="mk-step-number is-40">{index + 1}</span>
          <h3 className="mk-journey-step-title">{title}</h3>
          <p className="mk-journey-step-line">{line}</p>
        </li>)}
      </ol>
    </div>
  </section>;
}

export default function HowItWorksPage() {
  return <>
    <section className="mk-container mk-hero is-bottom" aria-labelledby="how-title">
      <div className="mk-hero-copy is-gap-24">
        <h1 className="mk-display" id="how-title">How it works</h1>
        <p className="mk-lead">Book a repair, or find jobs as a trade. Here is what happens, step by step.</p>
      </div>
      <nav className="mk-jump-list" aria-label="On this page">
        <ul>{jumpLinks.map(([icon, label, id]) => <li key={id}><a className="mk-jump" href={`#${id}`}>
          <span className="mk-icon-tile is-40"><Icon name={icon} size={20}/></span>
          <span className="mk-jump-label">{label}</span>
          <Icon name="arrow-down" size={18} className="mk-jump-arrow"/>
        </a></li>)}</ul>
      </nav>
    </section>

    {journeys.map((journey) => <Journey key={journey.id} journey={journey}/>)}

    <section className="mk-container mk-section" id="self-refer" aria-labelledby="self-refer-title">
      <h2 className="mk-h3 is-32" id="self-refer-title">Self refer</h2>
      <div className="mk-passport-block">
        <span className="mk-icon-tile is-56"><Icon name="house" size={28}/></span>
        <div className="mk-passport-block-text">
          <p className="mk-body">Tell us about your housing and support needs using our enquiry form. We’ll review your enquiry and contact you about next steps. No account or documents needed.</p>
          <ButtonLink to="/enquire" className="mk-self-referral-cta">Self Referral<Icon name="arrow-right" size={18}/></ButtonLink>
        </div>
      </div>
    </section>

    <section className="mk-container mk-section mk-questions" id="common-questions" aria-labelledby="questions-title">
      <h2 className="mk-h2 mk-questions-title" id="questions-title">Common Questions</h2>
      <dl className="mk-question-list">{questions.map(([question, answer]) => <div className="mk-question-card" key={question}>
        <dt>{question}</dt>
        <dd>{answer}</dd>
      </div>)}</dl>
    </section>

    <ClosingBand title="Get Started" text="Book a repair, or find repair jobs as a trade." wideCopy>
      <ButtonLink to={appLinks.createAccount} variant="white" size={56}>Create an account</ButtonLink>
    </ClosingBand>
  </>;
}
